import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  FiPlus,
  FiLayers,
  FiCheckCircle,
  FiGrid,
  FiList,
  FiChevronDown,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiTrash2,
  FiBriefcase,
  FiUsers,
  FiBarChart2,
  FiActivity,
  FiAlertTriangle,
  FiTarget
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { get, del } from "../../services/ApiService";
import { getBugsByProject } from "../../services/BugService";
import Header from "../../components/Header/Header";
import "./Dashboard.css";
import Modal from "../../components/Modal/Modal";
import { showToast } from "../../ui/toast";

const PREF_KEY_BASE = "buglog:dashboard:prefs";

const prefsKey = (companyName) => {
  const firm = companyName ? decodeURIComponent(companyName) : "__all__";
  return `${PREF_KEY_BASE}:${encodeURIComponent(firm)}`;
};

const readPrefs = (companyName) => {
  try {
    const raw = localStorage.getItem(prefsKey(companyName)) || "";
    if (!raw) {
      return {
        view: "grid",
        sort: "recent",
        onlyOpen: false,
        onlyWorked: false,
        query: "",
        page: 1
      };
    }
    const obj = JSON.parse(raw);
    return {
      view: obj?.view === "list" ? "list" : "grid",
      sort: obj?.sort === "az" ? "az" : obj?.sort === "za" ? "za" : "recent",
      onlyOpen: !!obj?.onlyOpen,
      onlyWorked: !!obj?.onlyWorked,
      query: typeof obj?.query === "string" ? obj.query : "",
      page: Number.isFinite(Number(obj?.page)) ? Math.max(1, Number(obj.page)) : 1
    };
  } catch {
    return {
      view: "grid",
      sort: "recent",
      onlyOpen: false,
      onlyWorked: false,
      query: "",
      page: 1
    };
  }
};

const writePrefs = (companyName, next) => {
  try {
    localStorage.setItem(prefsKey(companyName), JSON.stringify(next));
  } catch { }
};

const toBool = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "yes";

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const readCurrentUserId = () =>
  normalizeId(
    localStorage.getItem("user_id") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("uid") ||
      Cookies.get("user_id") ||
      Cookies.get("uid") ||
      ""
  );

const canManageProject = (project) => {
  const currentUserId = readCurrentUserId();
  const creatorId = normalizeId(
    project?.created_by ??
      project?.created_by_id ??
      project?.owner_id ??
      project?.user_id
  );
  const rawRole = String(
    project?.role ??
      project?.member_role ??
      project?.project_role ??
      project?.permission ??
      ""
  )
    .trim()
    .toLowerCase();

  if (
    toBool(project?.is_owner) ||
    toBool(project?.owner) ||
    toBool(project?.is_admin) ||
    toBool(project?.admin) ||
    toBool(project?.is_manager) ||
    toBool(project?.manager)
  ) {
    return true;
  }

  if (["owner", "admin", "administrator", "manager", "yönetici", "yonetici"].includes(rawRole)) {
    return true;
  }

  if (currentUserId && creatorId && currentUserId === creatorId) {
    return true;
  }

  return false;
};

const CalendarStrip = ({ events }) => {
  const container = useRef(null);
  const today = new Date();

  const pastCount = 7;
  const futureCount = 14;

  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  start.setDate(today.getDate() - pastCount);

  const todayMid = new Date(today);
  todayMid.setHours(0, 0, 0, 0);

  const keyOf = (d) => d.toISOString().slice(0, 10);

  const days = Array.from({ length: pastCount + 1 + futureCount }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });

  const scroll = (dir) => {
    if (!container.current) return;
    container.current.scrollBy({ left: dir * 320, behavior: "smooth" });
  };

  return (
    <section className="cal-strip-wrapper">
      <button className="cal-nav left" onClick={() => scroll(-1)} type="button">
        <FiChevronLeft size={18} />
      </button>
      <div className="cal-strip" ref={container}>
        {days.map((d, i) => {
          const key = keyOf(d);
          const count = events[key] || 0;
          const isToday = key === keyOf(today);
          const isPast = d < todayMid && !isToday;
          return (
            <div
              key={i}
              className={`day-chip ${isToday ? "today" : ""} ${count ? "has" : ""} ${isPast ? "past" : ""}`}
              title={d.toLocaleDateString("tr-TR")}
            >
              <span className="day">
                {d.toLocaleString("tr-TR", { weekday: "short" })}
              </span>
              <span className="date">{d.getDate()}</span>
              {count > 0 && <span className="cnt">{count}</span>}
            </div>
          );
        })}
      </div>
      <button className="cal-nav right" onClick={() => scroll(1)} type="button">
        <FiChevronRight size={18} />
      </button>
    </section>
  );
};

const getCover = (uniq) => {
  try {
    return uniq ? localStorage.getItem(`cover:${uniq}`) || "" : "";
  } catch {
    return "";
  }
};

const getProjectTitle = (p) => {
  try {
    const key = p?.uniq || p?.id;
    const ls = key ? localStorage.getItem(`title:${key}`) || "" : "";
    if (ls && String(ls).trim()) return String(ls).trim();
  } catch { }
  return p?.name || "";
};

const getProjectColor = (p) => {
  try {
    const ls = p?.uniq ? localStorage.getItem(`color:${p.uniq}`) : "";
    return ls || p?.color || "";
  } catch {
    return p?.color || "";
  }
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return hex;
  let h = hex.trim();
  if (h.startsWith("rgba(") || h.startsWith("rgb(")) return h;
  if (h[0] === "#") h = h.slice(1);
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16);
    const g = parseInt(h[1] + h[1], 16);
    const b = parseInt(h[2] + h[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  if (h.length === 6) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return hex;
};

const PAGE_SIZE = 12;
const RECENT_BUGS_PAGE_SIZE = 3;

const getDeadline = (p) =>
  p?.deadline || p?.deadline_date || p?.deadline_at || p?.end_date || null;

const isPastDate = (value) => {
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  d.setHours(23, 59, 59, 999);
  return d.getTime() < Date.now();
};

const formatTR = (val) => {
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
};

const formatTRDateTime = (val) => {
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

const decodeHtmlEntities = (value = "") => {
  const raw = String(value || "");
  try {
    if (typeof document !== "undefined") {
      const textarea = document.createElement("textarea");
      textarea.innerHTML = raw;
      return textarea.value || textarea.textContent || "";
    }
  } catch {
  }

  return raw
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'");
};

const htmlToText = (value = "") => {
  if (!value) return "";
  try {
    let str = String(value);
    str = str.replace(/<li[^>]*>/gi, "• ");
    str = str.replace(/<br\s*\/?>/gi, "\n");
    str = str.replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, "\n");
    str = str.replace(/<[^>]*>/g, "");
    str = decodeHtmlEntities(str);
    str = str.replace(/\u00a0/g, " ");
    return str
      .split("\n")
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter((line, index, arr) => !(line === "" && arr[index - 1] === ""))
      .join("\n")
      .trim();
  } catch {
    return String(value || "")
      .replace(/<li[^>]*>/gi, "• ")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, " ")
      .replace(/&#160;/gi, " ")
      .replace(/\u00a0/g, " ")
      .split("\n")
      .map((line) => line.replace(/\s+/g, " ").trim())
      .filter((line, index, arr) => !(line === "" && arr[index - 1] === ""))
      .join("\n")
      .trim();
  }
};

const getBugTimestampMs = (bug) => {
  const value =
    bug?.updated_at ||
    bug?.created_at ||
    bug?.date ||
    bug?.created ||
    bug?.last_activity_at ||
    "";
  const d = new Date(value);
  if (!value || Number.isNaN(d.getTime())) return 0;
  return d.getTime();
};

const getBugTitle = (bug) => String(bug?.title || bug?.bug_title || "İsimsiz Bug").trim();

const getBugPreview = (bug) =>
  htmlToText(bug?.description || bug?.bug_description || "")
    .replace(/\s+/g, " ")
    .trim();

const NO_COMPANY_LABEL = "Firma Yok";

const firmNameOf = (p) => {
  const fromApi =
    p?.company_name ||
    p?.company ||
    p?.firm ||
    p?.organization ||
    p?.org ||
    p?.client ||
    "";
  if (fromApi && String(fromApi).trim()) return String(fromApi).trim();
  try {
    const key = p?.uniq || p?.id;
    const ls = key ? localStorage.getItem(`project-company:${key}`) || "" : "";
    if (ls && String(ls).trim()) return String(ls).trim();
  } catch { }
  return "";
};

const firmDisplayName = (p) => {
  const n = firmNameOf(p);
  return n ? n : NO_COMPANY_LABEL;
};

const projectTypeOf = (p) => {
  const raw = String(p?.project_type || p?.type || "").trim().toLowerCase();
  if (raw === "company") return "company";
  if (raw === "normal") return "normal";
  return firmNameOf(p) ? "company" : "normal";
};

const isCompanyProject = (p) => projectTypeOf(p) === "company";

const groupProjects = (items) => {
  const companyGroups = new Map();
  const normalProjects = [];

  items.forEach((project) => {
    if (isCompanyProject(project)) {
      const firmName = firmDisplayName(project);
      if (!companyGroups.has(firmName)) {
        companyGroups.set(firmName, []);
      }
      companyGroups.get(firmName).push(project);
      return;
    }

    normalProjects.push(project);
  });

  const sections = Array.from(companyGroups.entries()).map(([name, sectionProjects]) => ({
    key: `company:${name}`,
    type: "company",
    kicker: "Şirket Projeleri",
    title: name,
    projects: sectionProjects
  }));

  if (normalProjects.length > 0) {
    sections.push({
      key: "normal",
      type: "normal",
      kicker: "Genel Projeler",
      title: "Bağımsız Projeler",
      projects: normalProjects
    });
  }

  return sections;
};

const createdAtMs = (p) => {
  const v = p?.created_at || p?.createdAt || p?.created || "";
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return 0;
  return d.getTime();
};

const lastWorkMs = (p) => {
  const v =
    p?.last_bug_date ||
    p?.last_bug_at ||
    p?.last_activity_at ||
    p?.last_job_date ||
    "";
  const d = new Date(v);
  if (!v || Number.isNaN(d.getTime())) return 0;
  return d.getTime();
};

const hasAnyWork = (p) => {
  const total = Number(p?.total_bugs || 0);
  if (total > 0) return true;
  if (lastWorkMs(p) > 0) return true;
  return false;
};

const openBugCountOf = (project) => Number(project?.incomplete_bugs_count || 0);
const totalBugCountOf = (project) => Number(project?.total_bugs || 0);

const projectProgressOf = (project) => {
  const total = totalBugCountOf(project);
  const open = openBugCountOf(project);
  if (total <= 0) return open > 0 ? 0 : 100;
  return Math.max(0, Math.min(100, Math.round(((total - open) / total) * 100)));
};

const formatRatio = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  if (Math.abs(n - Math.round(n)) < 0.05) return String(Math.round(n));
  return n.toFixed(1);
};

const Dashboard = () => {
  const nav = useNavigate();
  const { companyName } = useParams();

  const initialPrefs = useMemo(() => readPrefs(companyName), [companyName]);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState(() => initialPrefs.view);
  const [sort, setSort] = useState(() => initialPrefs.sort);
  const [onlyOpen, setOnlyOpen] = useState(() => initialPrefs.onlyOpen);
  const [onlyWorked, setOnlyWorked] = useState(() => initialPrefs.onlyWorked);
  const [query, setQuery] = useState(() => initialPrefs.query);
  const [page, setPage] = useState(() => initialPrefs.page);
  const [recentBugs, setRecentBugs] = useState([]);
  const [recentBugsLoading, setRecentBugsLoading] = useState(true);
  const [recentBugsPage, setRecentBugsPage] = useState(1);

  const [deletingId, setDeletingId] = useState(null);

  const [confirmDlg, setConfirmDlg] = useState({
    open: false,
    project: null
  });

  const [titleTick, setTitleTick] = useState(0);

  useEffect(() => {
    const onChanged = () => {
      setTitleTick((x) => x + 1);
    };
    const onStorage = (e) => {
      if (typeof e?.key === "string" && e.key.startsWith("title:")) {
        setTitleTick((x) => x + 1);
      }
      if (e?.key === prefsKey(companyName)) {
        const p = readPrefs(companyName);
        setView(p.view);
        setSort(p.sort);
        setOnlyOpen(p.onlyOpen);
        setOnlyWorked(p.onlyWorked);
        setQuery(p.query);
        setPage(p.page);
      }
    };
    window.addEventListener("project:changed", onChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("project:changed", onChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [companyName]);

  useEffect(() => {
    writePrefs(companyName, { view, sort, onlyOpen, onlyWorked, query, page });
  }, [companyName, view, sort, onlyOpen, onlyWorked, query, page]);

  useEffect(() => {
    const p = readPrefs(companyName);
    setView(p.view);
    setSort(p.sort);
    setOnlyOpen(p.onlyOpen);
    setOnlyWorked(p.onlyWorked);
    setQuery(p.query);
    setPage(p.page);
  }, [companyName]);

  const fetchAll = async () => {
    setLoading(true);
    const res = await get("/projects");
    if (res?.status === "success" && Array.isArray(res.projects)) {
      setProjects(res.projects);
    } else {
      setProjects([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const firmFilter = useMemo(
    () => (companyName ? decodeURIComponent(companyName) : null),
    [companyName]
  );

  const effectiveProjects = useMemo(() => {
    if (!firmFilter) return projects;
    return projects.filter((p) => firmDisplayName(p) === firmFilter.trim());
  }, [projects, firmFilter]);

  const stats = useMemo(
    () => ({
      total: effectiveProjects.length,
      company: effectiveProjects.filter((p) => isCompanyProject(p)).length,
      normal: effectiveProjects.filter((p) => !isCompanyProject(p)).length,
      open: effectiveProjects.reduce((a, p) => a + (p.incomplete_bugs_count || 0), 0),
      done: effectiveProjects.reduce(
        (a, p) => a + ((p.incomplete_bugs_count || 0) ? 0 : 1),
        0
      )
    }),
    [effectiveProjects]
  );

  const companySummaries = useMemo(() => {
    const map = new Map();

    effectiveProjects
      .filter((project) => isCompanyProject(project))
      .forEach((project) => {
        const name = firmDisplayName(project);
        if (!name || name === NO_COMPANY_LABEL) return;

        const key = name.toLocaleLowerCase("tr-TR");
        const current = map.get(key) || {
          key,
          name,
          projects: [],
          projectCount: 0,
          openCount: 0,
          cleanCount: 0,
          manageableCount: 0,
          latestMs: 0
        };

        const openCount = Number(project?.incomplete_bugs_count || 0);
        const activityMs = Math.max(lastWorkMs(project), createdAtMs(project));

        current.projects.push(project);
        current.projectCount += 1;
        current.openCount += Number.isFinite(openCount) ? openCount : 0;
        current.cleanCount += openCount > 0 ? 0 : 1;
        current.manageableCount += canManageProject(project) ? 1 : 0;
        current.latestMs = Math.max(current.latestMs, activityMs);

        map.set(key, current);
      });

    return Array.from(map.values())
      .map((company) => ({
        ...company,
        projects: [...company.projects].sort((a, b) => {
          const bw = Math.max(lastWorkMs(b), createdAtMs(b));
          const aw = Math.max(lastWorkMs(a), createdAtMs(a));
          if (bw !== aw) return bw - aw;
          return String(getProjectTitle(a)).localeCompare(String(getProjectTitle(b)), "tr-TR");
        })
      }))
      .sort((a, b) => {
        if (b.latestMs !== a.latestMs) return b.latestMs - a.latestMs;
        return a.name.localeCompare(b.name, "tr-TR");
      });
  }, [effectiveProjects]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return effectiveProjects.filter((p) => {
      const name = getProjectTitle(p);
      const openOk = !onlyOpen || (p.incomplete_bugs_count || 0);
      const workedOk = !onlyWorked || hasAnyWork(p);

      const queryOk =
        q === "" ||
        String(name || "").toLowerCase().includes(q) ||
        String(p.description || "").toLowerCase().includes(q) ||
        String(firmNameOf(p) || "").toLowerCase().includes(q);

      return openOk && workedOk && queryOk;
    });
  }, [effectiveProjects, onlyOpen, onlyWorked, query, titleTick]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      const an = getProjectTitle(a) || "";
      const bn = getProjectTitle(b) || "";

      if (onlyWorked) {
        const bw = lastWorkMs(b);
        const aw = lastWorkMs(a);
        if (bw !== aw) return bw - aw;

        const bc = createdAtMs(b);
        const ac = createdAtMs(a);
        if (bc !== ac) return bc - ac;

        return bn.localeCompare(an);
      }

      if (sort === "az") return an.localeCompare(bn);
      if (sort === "za") return bn.localeCompare(an);
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return list;
  }, [filtered, sort, onlyWorked, titleTick]);

  const groupedSorted = useMemo(() => groupProjects(sorted), [sorted]);
  const orderedProjects = useMemo(
    () => groupedSorted.flatMap((section) => section.projects),
    [groupedSorted]
  );

  useEffect(() => {
    setPage(1);
  }, [query, onlyOpen, onlyWorked, sort, view, firmFilter]);

  const totalPages = Math.max(1, Math.ceil(orderedProjects.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const paginated = orderedProjects.slice(start, end);
  const paginatedSections = useMemo(() => groupProjects(paginated), [paginated]);
  const recentBugsTotalPages = Math.max(
    1,
    Math.ceil(recentBugs.length / RECENT_BUGS_PAGE_SIZE)
  );
  const safeRecentBugsPage = Math.min(recentBugsPage, recentBugsTotalPages);
  const recentBugsStart = (safeRecentBugsPage - 1) * RECENT_BUGS_PAGE_SIZE;
  const recentBugsEnd = recentBugsStart + RECENT_BUGS_PAGE_SIZE;
  const recentBugsPaginated = recentBugs.slice(recentBugsStart, recentBugsEnd);

  const projectLookup = useMemo(() => {
    const map = new Map();
    effectiveProjects.forEach((project) => {
      const key = String(project?.uniq || project?.id || "").trim();
      if (key) {
        map.set(key, project);
      }
    });
    return map;
  }, [effectiveProjects]);

  const report = useMemo(() => {
    const projectKeys = new Set(
      effectiveProjects
        .map((project) => String(project?.uniq || project?.id || "").trim())
        .filter(Boolean)
    );

    const bugStatsByProject = new Map();
    const relevantRecentBugs = recentBugs.filter((bug) => {
      const key = String(bug?.projectKey || "").trim();
      return !projectKeys.size || (key && projectKeys.has(key));
    });

    relevantRecentBugs.forEach((bug) => {
      const key = String(bug?.projectKey || "").trim();
      if (!key) return;

      const current = bugStatsByProject.get(key) || {
        total: 0,
        open: 0,
        lastMs: 0
      };
      const isDone = toBool(bug?.is_completed) || toBool(bug?.completed);

      current.total += 1;
      if (!isDone) current.open += 1;
      current.lastMs = Math.max(current.lastMs, getBugTimestampMs(bug));
      bugStatsByProject.set(key, current);
    });

    const projectStats = effectiveProjects.map((project) => {
      const key = String(project?.uniq || project?.id || "").trim();
      const fromBugs = bugStatsByProject.get(key) || { total: 0, open: 0, lastMs: 0 };
      const apiOpen = openBugCountOf(project);
      const apiTotal = totalBugCountOf(project);
      const total = Math.max(apiTotal, fromBugs.total, apiOpen, fromBugs.open);
      const open = Math.min(total, Math.max(apiOpen, fromBugs.open));

      return {
        project,
        key,
        total,
        open,
        completed: Math.max(0, total - open),
        progress: total > 0 ? Math.max(0, Math.min(100, Math.round(((total - open) / total) * 100))) : projectProgressOf(project),
        latestMs: Math.max(fromBugs.lastMs, lastWorkMs(project), createdAtMs(project))
      };
    });

    const totalProjects = effectiveProjects.length;
    const openBugs = projectStats.reduce((acc, item) => acc + item.open, 0);
    const totalBugs = projectStats.reduce((acc, item) => acc + item.total, 0);
    const completedBugs = projectStats.reduce((acc, item) => acc + item.completed, 0);
    const cleanProjects = projectStats.filter((item) => item.open === 0).length;
    const workedProjects = projectStats.filter((item) => item.total > 0 || hasAnyWork(item.project)).length;
    const overdueProjects = projectStats.filter((item) => {
      const deadline = getDeadline(item.project);
      return deadline && isPastDate(deadline) && item.open > 0;
    });
    const completionRate = totalBugs > 0 ? Math.round((completedBugs / totalBugs) * 100) : 0;
    const openAverage = totalProjects > 0 ? openBugs / totalProjects : 0;
    const recentWindowStart = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const recent7Days = relevantRecentBugs.filter((bug) => getBugTimestampMs(bug) >= recentWindowStart).length;
    const recentOpen = relevantRecentBugs.filter((bug) => !(toBool(bug?.is_completed) || toBool(bug?.completed))).length;

    const topRiskProjects = projectStats
      .map((item) => {
        const deadline = getDeadline(item.project);
        return {
          project: item.project,
          name: getProjectTitle(item.project),
          openCount: item.open,
          totalCount: item.total,
          progress: item.progress,
          overdue: Boolean(deadline && isPastDate(deadline) && item.open > 0),
          latestMs: item.latestMs
        };
      })
      .filter((item) => item.openCount > 0)
      .sort((a, b) => {
        if (Number(b.overdue) !== Number(a.overdue)) return Number(b.overdue) - Number(a.overdue);
        if (b.openCount !== a.openCount) return b.openCount - a.openCount;
        return b.latestMs - a.latestMs;
      })
      .slice(0, 4);

    const activityMap = new Map();
    relevantRecentBugs.forEach((bug) => {
      const key = String(bug?.projectKey || "").trim();
      if (!key) return;
      const current = activityMap.get(key) || {
        key,
        projectName: bug?.projectName || getProjectTitle(projectLookup.get(key) || {}),
        count: 0,
        open: 0,
        lastMs: 0
      };
      current.count += 1;
      if (!(toBool(bug?.is_completed) || toBool(bug?.completed))) current.open += 1;
      current.lastMs = Math.max(current.lastMs, getBugTimestampMs(bug));
      activityMap.set(key, current);
    });

    const activeProjects = Array.from(activityMap.values())
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return b.lastMs - a.lastMs;
      })
      .slice(0, 4);

    const health =
      totalBugs === 0 && openBugs === 0
        ? "good"
      : overdueProjects.length > 0 || completionRate < 45
        ? "risk"
        : openBugs > 0 || completionRate < 75
          ? "watch"
          : "good";

    const insight =
      overdueProjects.length > 0
        ? `${overdueProjects.length} projede son tarih geçmiş açık iş var. Önce bu projeleri kapatmak en yüksek etkiyi verir.`
        : topRiskProjects.length > 0
          ? `${topRiskProjects[0].name} şu an en yüksek açık iş yüküne sahip. İlk odak noktası burası olabilir.`
          : totalProjects > 0
            ? "Görünümde açık iş yükü düşük. Yeni iş açıldıkça rapor paneli otomatik güncellenecek."
            : "Henüz raporlanacak proje yok. İlk projeyi eklediğinde bu alan dolacak.";

    return {
      totalProjects,
      openBugs,
      totalBugs,
      completedBugs,
      cleanProjects,
      workedProjects,
      overdueCount: overdueProjects.length,
      completionRate,
      openAverage,
      recent7Days,
      recentOpen,
      topRiskProjects,
      activeProjects,
      health,
      insight
    };
  }, [effectiveProjects, projectLookup, recentBugs]);

  useEffect(() => {
    if (safePage !== page) setPage(safePage);
  }, [safePage, page]);

  useEffect(() => {
    if (safeRecentBugsPage !== recentBugsPage) {
      setRecentBugsPage(safeRecentBugsPage);
    }
  }, [safeRecentBugsPage, recentBugsPage]);

  useEffect(() => {
    setRecentBugsPage(1);
  }, [firmFilter]);

  const initials = (n) =>
    (n || "")
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0])
      .join("")
      .toUpperCase();

  const events = useMemo(() => {
    const map = {};
    effectiveProjects.forEach((p) => {
      const key = (p.last_bug_date || p.created_at || "").slice(0, 10);
      if (!key) return;
      const inc = p.incomplete_bugs_count || 0;
      map[key] = (map[key] || 0) + inc;
    });
    return map;
  }, [effectiveProjects]);

  const askDeleteProject = (project) => {
    setConfirmDlg({ open: true, project });
  };

  const handleDeleteProject = async (project) => {
    if (!project?.uniq) {
      alert("Projeye ait uniq değeri bulunamadı. Silme işlemi için \"uniq\" gerekiyor.");
      return;
    }

    const uniq = project.uniq;
    setDeletingId(uniq);

    const prev = projects;
    setProjects((p) => p.filter((x) => x.uniq !== uniq));

    const res = await del("/projects", { project_id: uniq });
    setDeletingId(null);

    if (res?.status !== "success") {
      setProjects(prev);
      showToast({
        title: "Silme başarısız",
        description: res?.message || "Proje silinemedi.",
        variant: "error"
      });
    } else {
      try {
        localStorage.removeItem(`cover:${uniq}`);
        localStorage.removeItem(`title:${uniq}`);
      } catch { }
      showToast({
        title: "Proje silindi",
        description: `“${getProjectTitle(project)}” kaldırıldı.`,
        variant: "success"
      });
    }
  };

  const goPage = (n) => {
    const next = Math.min(Math.max(1, n), totalPages);
    if (next !== page) {
      setPage(next);
      try {
        document
          .querySelector(".dash-list")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch { }
    }
  };

  const visiblePages = useMemo(() => {
    const pages = [];
    const windowSize = 1;
    const add = (n) => pages.push(n);
    add(1);
    for (
      let i = Math.max(2, page - windowSize);
      i <= Math.min(totalPages - 1, page + windowSize);
      i++
    ) {
      add(i);
    }
    if (totalPages > 1) add(totalPages);
    return [...new Set(pages)].sort((a, b) => a - b);
  }, [page, totalPages]);

  const recentVisiblePages = useMemo(() => {
    const pages = [];
    const windowSize = 1;
    const add = (n) => pages.push(n);
    add(1);
    for (
      let i = Math.max(2, safeRecentBugsPage - windowSize);
      i <= Math.min(recentBugsTotalPages - 1, safeRecentBugsPage + windowSize);
      i++
    ) {
      add(i);
    }
    if (recentBugsTotalPages > 1) add(recentBugsTotalPages);
    return [...new Set(pages)].sort((a, b) => a - b);
  }, [safeRecentBugsPage, recentBugsTotalPages]);

  const hasAnyOverdue = useMemo(
    () =>
      effectiveProjects.some((p) => {
        const dl = getDeadline(p);
        return dl && isPastDate(dl) && (p.incomplete_bugs_count || 0) > 0;
      }),
    [effectiveProjects]
  );

  useEffect(() => {
    const root = document.documentElement;
    if (hasAnyOverdue) {
      root.classList.add("theme-overdue");
    } else {
      root.classList.remove("theme-overdue");
    }
    return () => root.classList.remove("theme-overdue");
  }, [hasAnyOverdue]);

  useEffect(() => {
    let active = true;

    const loadRecentBugs = async () => {
      const projectsWithKey = effectiveProjects.filter((project) =>
        String(project?.uniq || project?.id || "").trim()
      );

      if (!projectsWithKey.length) {
        if (!active) return;
        setRecentBugs([]);
        setRecentBugsLoading(false);
        return;
      }

      setRecentBugsLoading(true);

      const grouped = await Promise.all(
        projectsWithKey.map(async (project) => {
          const projectKey = String(project?.uniq || project?.id || "").trim();
          if (!projectKey) return [];

          try {
            const items = await getBugsByProject(projectKey);
            if (!Array.isArray(items)) return [];

            return items.map((bug) => ({
              ...bug,
              projectKey,
              projectName: getProjectTitle(project),
              canManage: canManageProject(project)
            }));
          } catch {
            return [];
          }
        })
      );

      if (!active) return;

      const latest = grouped
        .flat()
        .sort((a, b) => getBugTimestampMs(b) - getBugTimestampMs(a));

      setRecentBugs(latest);
      setRecentBugsLoading(false);
    };

    loadRecentBugs();

    return () => {
      active = false;
    };
  }, [effectiveProjects, titleTick]);

  const openRecentBug = (bug) => {
    const key = String(bug?.projectKey || "").trim();
    const project = projectLookup.get(key);
    const projectName = bug?.projectName || getProjectTitle(project || {});
    const basePath = `/bugs/${key}/${bug?.canManage ? 1 : canManageProject(project || {}) ? 1 : 0}/${encodeURIComponent(projectName)}`;
    const bugId = String(bug?.id || bug?.bug_id || "").trim();
    nav(bugId ? `${basePath}?bug=${encodeURIComponent(bugId)}` : basePath);
  };

  const goRecentBugsPage = (n) => {
    const next = Math.min(Math.max(1, n), recentBugsTotalPages);
    if (next !== recentBugsPage) {
      setRecentBugsPage(next);
    }
  };

  const addProjectToCompany = (company) => {
    const name = String(company || "").trim();
    if (!name || name === NO_COMPANY_LABEL) {
      nav("/add-company");
      return;
    }
    nav(`/add-company/${encodeURIComponent(name)}/project`);
  };

  return (
    <>
      <Header buttons={[]} />
      <main className="dash">
        <section className="dash-hero">
          <div className="dash-hero-copy">
            <span className="dash-hero-kicker">
              {firmFilter ? "Firma Görünümü" : "Buglog Dashboard"}
            </span>
            <h1>{firmFilter ? `${firmFilter} projeleri` : "Projelerini tek akışta yönet"}</h1>
            <p>
              {firmFilter
                ? `${firmFilter} için ${stats.total} proje arasından filtrelenmiş görünümü izliyorsun.`
                : "Açık işler, teslim tarihleri ve proje durumu tek merkezde toplandı."}
            </p>

            <div className="dash-hero-tags">
              <span className="dash-hero-tag">Toplam {stats.total} proje</span>
              <span className="dash-hero-tag">{sorted.length} görünümde</span>
              <span className="dash-hero-tag">{stats.company} şirket</span>
              <span className="dash-hero-tag">{stats.normal} normal</span>
              <span className="dash-hero-tag">{stats.open} açık iş</span>
              {firmFilter && (
                <button
                  className="dash-hero-link"
                  onClick={() => nav("/companies")}
                  title="Firmalar listesine dön"
                  type="button"
                >
                  Firmalar
                </button>
              )}
            </div>
          </div>

          <div className="dash-hero-panel">
            <div className="dash-hero-metric">
              <strong>{stats.done}</strong>
              <span>Tamamlanan Proje</span>
            </div>
            <div className="dash-hero-metric">
              <strong>{totalPages}</strong>
              <span>Sayfa</span>
            </div>
            <div className="dash-hero-actions">
              <button className="dash-hero-action" onClick={() => nav("/add-project")} type="button">
                <FiPlus size={18} />
                <span>Proje Ekle</span>
              </button>
              <button className="dash-hero-action secondary" onClick={() => nav("/add-company")} type="button">
                <FiBriefcase size={18} />
                <span>Şirket Ekle</span>
              </button>
            </div>
          </div>
        </section>

        <section className="recent-bugs">
          <div className="recent-bugs-head">
            <div className="recent-bugs-copy">
              <span className="recent-bugs-kicker">En Son İşler</span>
              <h2>Son gönderilen işler</h2>
              <p>Kimin ne gönderdiğini gör, karta tıklayıp doğrudan aksiyon al.</p>
            </div>
            <div className="recent-bugs-total">
              <strong>{recentBugs.length}</strong>
              <span>Toplam Kayıt</span>
            </div>
          </div>

          <div className="recent-bugs-list">
            {recentBugsLoading
              ? Array.from({ length: RECENT_BUGS_PAGE_SIZE }).map((_, index) => (
                <div key={index} className="recent-bug-card skeleton" />
              ))
              : recentBugs.length === 0
                ? (
                  <div className="recent-bugs-empty">
                    <strong>Henüz bug hareketi yok</strong>
                    <p>Projelerde yeni bir iş oluştuğunda burada son hareketler otomatik görünecek.</p>
                  </div>
                )
                : recentBugsPaginated.map((bug) => {
                  const preview = getBugPreview(bug);
                  const done = toBool(bug?.is_completed) || toBool(bug?.completed);
                  const dateLabel = formatTRDateTime(
                    bug?.updated_at || bug?.created_at || bug?.date || bug?.created
                  );
                  const versionLabel = String(bug?.version_name || bug?.version || "").trim();
                  const reporter = String(
                    bug?.email || bug?.user_email || bug?.created_by_email || ""
                  ).trim();
                  const reporterInitial = reporter ? reporter.charAt(0).toUpperCase() : "";

                  return (
                    <button
                      key={`${bug.projectKey}-${bug.id || bug.bug_id || getBugTitle(bug)}`}
                      className="recent-bug-card"
                      onClick={() => openRecentBug(bug)}
                      type="button"
                    >
                      <div className="recent-bug-topline">
                        <span className={`recent-bug-state ${done ? "done" : "open"}`}>
                          {done ? "Tamamlandı" : "Açık"}
                        </span>
                        {dateLabel && <span className="recent-bug-date">{dateLabel}</span>}
                      </div>

                      <h3>{getBugTitle(bug)}</h3>

                      <p>{preview || "Bu bug için henüz detay girilmedi."}</p>

                      {reporter && (
                        <div className="recent-bug-reporter">
                          <span className="recent-bug-avatar" aria-hidden="true">{reporterInitial}</span>
                          <span className="recent-bug-reporter-mail">{reporter}</span>
                        </div>
                      )}

                      <div className="recent-bug-footer">
                        <div className="recent-bug-project">
                          <strong>{bug.projectName}</strong>
                          <span>{versionLabel || "Sürüm bilgisi yok"}</span>
                        </div>
                        <span className="recent-bug-link">Bug’a Git</span>
                      </div>
                    </button>
                  );
                })}
          </div>

          {!recentBugsLoading && recentBugs.length > 0 && (
            <div className="recent-bugs-pagination-wrap">
              <div className="recent-bugs-pageinfo">
                {recentBugsStart + 1}-{Math.min(recentBugsEnd, recentBugs.length)} / {recentBugs.length} kayıt
              </div>

              {recentBugsTotalPages > 1 && (
                <section className="dash-pagination recent-bugs-pagination" aria-label="Canlı akış sayfalama">
                  <button
                    className="page-btn"
                    onClick={() => goRecentBugsPage(safeRecentBugsPage - 1)}
                    disabled={safeRecentBugsPage <= 1}
                    aria-label="Önceki canlı akış sayfası"
                    type="button"
                  >
                    <FiChevronLeft size={16} />
                  </button>

                  {recentVisiblePages.map((pnum, idx) => {
                    const isActive = pnum === safeRecentBugsPage;
                    const prev = recentVisiblePages[idx - 1];
                    const needsDots = idx > 0 && pnum - prev > 1;
                    return (
                      <React.Fragment key={pnum}>
                        {needsDots && <span className="dots">…</span>}
                        <button
                          className={`page-btn ${isActive ? "active" : ""}`}
                          onClick={() => goRecentBugsPage(pnum)}
                          aria-current={isActive ? "page" : undefined}
                          type="button"
                        >
                          {pnum}
                        </button>
                      </React.Fragment>
                    );
                  })}

                  <button
                    className="page-btn"
                    onClick={() => goRecentBugsPage(safeRecentBugsPage + 1)}
                    disabled={safeRecentBugsPage >= recentBugsTotalPages}
                    aria-label="Sonraki canlı akış sayfası"
                    type="button"
                  >
                    <FiChevronRight size={16} />
                  </button>
                </section>
              )}
            </div>
          )}
        </section>

        <section className="dash-stats">
          <div className="stat-card">
            <FiLayers size={22} />
            <span>Proje</span>
            <strong>{stats.total}</strong>
          </div>
          <div className="stat-card">
            <FiGrid size={22} />
            <span>Şirket Projesi</span>
            <strong>{stats.company}</strong>
          </div>
          <div className="stat-card">
            <FiPlus size={22} />
            <span>Açık İş</span>
            <strong>{stats.open}</strong>
          </div>
          <div className="stat-card">
            <FiCheckCircle size={22} />
            <span>Tamamlanan</span>
            <strong>{stats.done}</strong>
          </div>
        </section>

        <section className={`dash-report ${report.health}`}>
          <div className="dash-report-head">
            <div>
              <span className="dash-report-kicker">
                <FiBarChart2 size={14} />
                Rapor
              </span>
              <h2>Operasyon Özeti</h2>
              <p>
                Mevcut proje ve bug verilerinden açık iş yükü, tamamlanma oranı ve son hareketleri özetler.
              </p>
            </div>

            <div className="dash-report-score">
              <span>Tamamlanma</span>
              <strong>{report.completionRate}%</strong>
            </div>
          </div>

          <div className="report-metric-grid">
            <article className="report-metric-card">
              <FiTarget size={20} />
              <span>Toplam İş</span>
              <strong>{report.totalBugs}</strong>
              <small>{report.completedBugs} tamamlandı</small>
            </article>
            <article className="report-metric-card">
              <FiActivity size={20} />
              <span>Açık Yük</span>
              <strong>{report.openBugs}</strong>
              <small>Proje başı {formatRatio(report.openAverage)} açık iş</small>
            </article>
            <article className="report-metric-card">
              <FiAlertTriangle size={20} />
              <span>Geciken</span>
              <strong>{report.overdueCount}</strong>
              <small>Son tarihi geçmiş proje</small>
            </article>
            <article className="report-metric-card">
              <FiCheckCircle size={20} />
              <span>Temiz Proje</span>
              <strong>{report.cleanProjects}</strong>
              <small>{report.workedProjects} proje hareket gördü</small>
            </article>
          </div>

          <div className="report-insight">
            <FiActivity size={17} />
            <span>{report.insight}</span>
          </div>

          <div className="report-columns">
            <div className="report-panel">
              <div className="report-panel-head">
                <h3>Riskli Projeler</h3>
                <span>{report.topRiskProjects.length} kayıt</span>
              </div>

              {report.topRiskProjects.length > 0 ? (
                <div className="report-list">
                  {report.topRiskProjects.map((item) => (
                    <button
                      type="button"
                      className={`report-list-item${item.overdue ? " overdue" : ""}`}
                      key={item.project.uniq || item.project.id || item.name}
                      onClick={() =>
                        nav(`/bugs/${item.project.uniq}/${canManageProject(item.project) ? 1 : 0}/${encodeURIComponent(item.name)}`)
                      }
                    >
                      <span className="report-list-main">
                        <strong>{item.name}</strong>
                        <small>{item.overdue ? "Son tarih geçti" : `${item.progress}% tamamlandı`}</small>
                      </span>
                      <span className="report-list-badge">{item.openCount} açık</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="report-empty">Açık iş yükü olan proje bulunmuyor.</div>
              )}
            </div>

            <div className="report-panel">
              <div className="report-panel-head">
                <h3>Aktif Projeler</h3>
                <span>Son 7 gün: {report.recent7Days}</span>
              </div>

              {report.activeProjects.length > 0 ? (
                <div className="report-list">
                  {report.activeProjects.map((item) => (
                    <button
                      type="button"
                      className="report-list-item"
                      key={item.key}
                      onClick={() => {
                        const project = projectLookup.get(item.key);
                        const name = item.projectName || getProjectTitle(project || {});
                        nav(`/bugs/${item.key}/${project && canManageProject(project) ? 1 : 0}/${encodeURIComponent(name || "Proje")}`);
                      }}
                    >
                      <span className="report-list-main">
                        <strong>{item.projectName || "Proje"}</strong>
                        <small>{item.open} açık hareket</small>
                      </span>
                      <span className="report-list-badge">{item.count} hareket</span>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="report-empty">Son hareketler yüklendiğinde aktif projeler burada görünür.</div>
              )}
            </div>
          </div>
        </section>

        {companySummaries.length > 0 && (
          <section className="company-panel">
            <div className="company-panel-head">
              <div>
                <span className="company-panel-kicker">Şirket Paneli</span>
                <h2>{firmFilter ? `${firmFilter} çalışma alanı` : "Şirket çalışma alanları"}</h2>
                <p>
                  Şirketlere bağlı projeleri, açık işleri ve son hareketleri tek alanda takip et.
                </p>
              </div>

              <div className="company-panel-actions">
                {firmFilter && (
                  <button type="button" className="company-panel-light" onClick={() => nav("/companies")}>
                    Tüm Şirketler
                  </button>
                )}
                <button type="button" onClick={() => nav("/add-company")}>
                  <FiBriefcase size={16} />
                  <span>Şirket Ekle</span>
                </button>
              </div>
            </div>

            <div className="company-panel-grid">
              {companySummaries.map((company) => {
                const visibleProjects = company.projects.slice(0, 3);
                const latestLabel = company.latestMs ? formatTRDateTime(company.latestMs) : "Hareket yok";

                return (
                  <article className="company-card" key={company.key}>
                    <div className="company-card-top">
                      <div className="company-mark">{initials(company.name) || "Ş"}</div>
                      <div className="company-card-title">
                        <span>Şirket</span>
                        <h3>{company.name}</h3>
                      </div>
                    </div>

                    <div className="company-metrics">
                      <div>
                        <strong>{company.projectCount}</strong>
                        <span>Proje</span>
                      </div>
                      <div>
                        <strong>{company.openCount}</strong>
                        <span>Açık İş</span>
                      </div>
                      <div>
                        <strong>{company.manageableCount}</strong>
                        <span>Yönetilebilir</span>
                      </div>
                    </div>

                    <div className="company-activity">
                      <FiUsers size={16} />
                      <span>{company.cleanCount} proje açık işsiz</span>
                      <small>{latestLabel}</small>
                    </div>

                    <div className="company-project-list">
                      {visibleProjects.map((project) => {
                        const name = getProjectTitle(project);
                        return (
                          <button
                            type="button"
                            key={project.uniq || project.id}
                            onClick={() =>
                              nav(`/bugs/${project.uniq}/${canManageProject(project) ? 1 : 0}/${encodeURIComponent(name)}`)
                            }
                          >
                            <span>{name}</span>
                            <strong>{Number(project?.incomplete_bugs_count || 0)} açık</strong>
                          </button>
                        );
                      })}
                    </div>

                    <div className="company-card-actions">
                      <button
                        type="button"
                        onClick={() => nav(`/companies/${encodeURIComponent(company.name)}`)}
                      >
                        Panele Git
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        onClick={() => addProjectToCompany(company.name)}
                      >
                        <FiPlus size={14} />
                        Proje Ekle
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}

        <CalendarStrip events={events} />

        <section className="dash-toolbar">
          <div className="search-box">
            <FiSearch size={16} />
            <input
              placeholder="Projede ara..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="view-toggle">
            <button
              className={view === "grid" ? "active" : ""}
              onClick={() => setView("grid")}
              type="button"
            >
              <FiGrid size={18} />
            </button>
            <button
              className={view === "list" ? "active" : ""}
              onClick={() => setView("list")}
              type="button"
            >
              <FiList size={18} />
            </button>
          </div>

          <div className="chips">
            <label
              className={`chip ${!onlyOpen && !onlyWorked ? "chosen" : ""}`}
              onClick={() => {
                setOnlyOpen(false);
                setOnlyWorked(false);
              }}
            >
              Hepsi
            </label>
            <label
              className={`chip ${onlyOpen && !onlyWorked ? "chosen" : ""}`}
              onClick={() => {
                setOnlyOpen(true);
                setOnlyWorked(false);
              }}
            >
              Açık İş
            </label>
            <label
              className={`chip ${onlyWorked ? "chosen" : ""}`}
              onClick={() => {
                setOnlyOpen(false);
                setOnlyWorked(true);
              }}
            >
              En Son Eklenen
            </label>
          </div>

          <div className="sort-box">
            <select value={sort} onChange={(e) => setSort(e.target.value)} disabled={onlyWorked}>
              <option value="recent">En Yeni</option>
              <option value="az">A-Z</option>
              <option value="za">Z-A</option>
            </select>
            <FiChevronDown size={16} />
          </div>
        </section>

        <section className={`dash-list ${view}`}>
          {loading
            ? Array.from({ length: PAGE_SIZE }).map((_, i) => (
              <div key={i} className="dash-card skeleton" />
            ))
            : paginated.length === 0
              ? (
                <div className="dash-empty">
                  <strong>Bu görünümde proje bulunamadı</strong>
                  <p>Arama ve filtreleri değiştirerek tekrar deneyebilir ya da yeni proje ekleyebilirsin.</p>
                  <button type="button" onClick={() => nav("/add-project")}>
                    <FiPlus size={16} />
                    <span>Yeni Proje</span>
                  </button>
                </div>
              )
            : paginatedSections.map((section) => (
              <React.Fragment key={section.key}>
                <div className="project-group-head">
                  <div className="project-group-title">
                    <span>{section.kicker}</span>
                    <h2>{section.title}</h2>
                  </div>
                  <div className="project-group-actions">
                    <strong>{section.projects.length} proje</strong>
                    {section.type === "company" && (
                      <button
                        type="button"
                        className="project-group-add"
                        onClick={() => addProjectToCompany(section.title)}
                      >
                        <FiPlus size={14} />
                        <span>Proje Ekle</span>
                      </button>
                    )}
                  </div>
                </div>

                {section.projects.map((p, i) => {
              const name = getProjectTitle(p);

              const progress =
                (p.incomplete_bugs_count || 0) === 0
                  ? 100
                  : Math.max(
                    0,
                    100 -
                    ((p.incomplete_bugs_count || 0) / (p.total_bugs || 1)) * 100
                  );

              const cover = getCover(p.uniq);
              const color = getProjectColor(p);
              const badgeBg = color ? hexToRgba(color, 0.12) : undefined;
              const badgeBorder = color ? hexToRgba(color, 0.35) : undefined;

              const deadline = getDeadline(p);
              const overdue =
                deadline && isPastDate(deadline) && (p.incomplete_bugs_count || 0) > 0;
              const projectType = projectTypeOf(p);
              const firmName = firmNameOf(p);

              return (
                <article
                  key={p.id ?? p.uniq}
                  className={`dash-card appear ${overdue ? "overdue" : ""}`}
                  style={{ animationDelay: `${i * 60}ms` }}
                  onClick={() =>
                    nav(`/bugs/${p.uniq}/${canManageProject(p) ? 1 : 0}/${encodeURIComponent(name)}`)
                  }
                >
                  <div
                    className={`dash-logo ${cover ? "has-cover" : ""}`}
                    style={
                      cover
                        ? { backgroundImage: `url(${cover})` }
                        : color
                          ? { background: `linear-gradient(135deg, ${color} 0%, ${color} 100%)` }
                          : undefined
                    }
                  >
                    {!cover && initials(name)}
                  </div>

                  <div className="card-actions">
                    <button
                      className="icon-btn danger"
                      title="Projeyi Sil"
                      onClick={(e) => {
                        e.stopPropagation();
                        askDeleteProject(p);
                      }}
                      disabled={deletingId === (p.uniq || p.id)}
                      type="button"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>

                  <div className="dash-body">
                    <h3>{name}</h3>
                    {p.description && <p>{p.description}</p>}
                    <div className="project-type-pill">
                      {projectType === "company" ? `Şirket Projesi${firmName ? ` • ${firmName}` : ""}` : "Genel Proje"}
                    </div>
                    <div className="progress-wrapper">
                      <div
                        className="progress-bar"
                        style={{
                          width: `${progress}%`,
                          ...(color ? { background: color } : {})
                        }}
                      />
                    </div>

                    {deadline && (
                      <div
                        className={`deadline-pill ${overdue ? "past" : ""}`}
                        title={`Son tarih: ${formatTR(deadline)}`}
                      >
                        Son tarih: {formatTR(deadline)}
                        {overdue ? " • Geçti" : ""}
                      </div>
                    )}
                  </div>

                  {(p.incomplete_bugs_count || 0) !== 0 && (
                    <span
                      className={`dash-badge ${overdue ? "late" : ""}`}
                      style={
                        !overdue && color
                          ? {
                            color,
                            borderColor: badgeBorder || color,
                            backgroundColor: badgeBg
                          }
                          : undefined
                      }
                    >
                      {p.incomplete_bugs_count}
                    </span>
                  )}
                </article>
              );
                })}
              </React.Fragment>
            ))}
        </section>

        <section className="dash-pagination" aria-label="Sayfalama">
          <button
            className="page-btn"
            onClick={() => goPage(page - 1)}
            disabled={page <= 1}
            aria-label="Önceki sayfa"
            type="button"
          >
            <FiChevronLeft size={16} />
          </button>

          {visiblePages.map((pnum, idx) => {
            const isActive = pnum === page;
            const prev = visiblePages[idx - 1];
            const needsDots = idx > 0 && pnum - prev > 1;
            return (
              <React.Fragment key={pnum}>
                {needsDots && <span className="dots">…</span>}
                <button
                  className={`page-btn ${isActive ? "active" : ""}`}
                  onClick={() => goPage(pnum)}
                  aria-current={isActive ? "page" : undefined}
                  type="button"
                >
                  {pnum}
                </button>
              </React.Fragment>
            );
          })}

          <button
            className="page-btn"
            onClick={() => goPage(page + 1)}
            disabled={page >= totalPages}
            aria-label="Sonraki sayfa"
            type="button"
          >
            <FiChevronRight size={16} />
          </button>
        </section>
      </main>

      <Modal
        open={confirmDlg.open}
        close={() => setConfirmDlg({ open: false, project: null })}
      >
        <h2>Projeyi Sil</h2>
        <p className="modal-desc">
          “{confirmDlg.project ? getProjectTitle(confirmDlg.project) : ""}” projesini silmek istediğinize emin misiniz?
        </p>
        <div className="modal-buttons">
          <button
            type="button"
            className="modal-close-button"
            onClick={() => setConfirmDlg({ open: false, project: null })}
          >
            İptal
          </button>
          <button
            type="button"
            className="modal-add-button danger"
            onClick={async () => {
              const p = confirmDlg.project;
              setConfirmDlg({ open: false, project: null });
              await handleDeleteProject(p);
            }}
          >
            Tamam
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Dashboard;
